<template>
  <IHOrderModal
    v-model:visible="actionModalVisible"
    :event="event"
    :action="ihOrder"
    :action-type="ActionType.IHOrder"
    :create-mode="!action"
    :loading="loading"
    :title="
      action
        ? t('license.ih.ih.IHOrderHeadlineChange').toString()
        : t('license.ih.ih.IHOrderHeadline').toString()
    "
    @ok="saveIHOrder"
    @select-technical-place="setTechnicalPlace"
    @select-technical-category="setTechnicalCategory"
    @update:visible="$emit('update:visible', $event)"
    @observer:valid="observerValid = $event"
  />
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { provide, ref, nextTick, type PropType, computed, inject } from "vue";
import { DateTime } from "luxon";
import {
  type EventListEventFieldsFragment,
  EventType,
  TaskStatus,
  useCreateIHOrderMutation,
  useUpdateIHOrderMutation,
  ActionType,
  type ActionListActionFields_IHOrder_Fragment,
  EABShorthandTaskDeadline,
} from "@/graphql";
import IHOrderModal from "@/components/event-action/action/modal/IHOrderModal.vue";
import { CreateInjectionKey } from "@/components/event-action/issue/IssueTypes";
import { TechnicalPlaceForEventModalInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import { addAction } from "@/graphql/apolloCacheUtils";
import { useIHConfig, useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import usePropsAsDefault from "@/functions/usePropsAsDefault";
import notEmpty from "@/functions/notEmpty";
import type { IHOrderEditType } from "@/components/event-action/task/helper/DefaultTask";
import { hasRight } from "@/functions/authorizationState";

const props = defineProps({
  event: {
    type: Object as PropType<
      EventListEventFieldsFragment & {
        technicalCategoryId: number | undefined;
        technicalPlaceId: number | undefined;
      }
    > | null,
    default: null,
  },
  action: {
    type: Object as PropType<ActionListActionFields_IHOrder_Fragment>,
    default: null,
  },
  visible: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:visible", "updateAction", "save", "hide"]);
const observerValid = ref(false);

const actionModalVisible = ref(props.visible);

const boardId = computed(
  () => props.event.boardConnection.find((con) => con.status === 0)?.boardId,
);

const { siteId, board } = useSpecifiedBoardContext(boardId);

const { resultIHConfig } = useIHConfig(siteId);

const technicalPlaceIdForEventModal = inject(TechnicalPlaceForEventModalInjectionKey, null);

const repTime = computed(() => {
  switch (resultIHConfig.value?.eabShorthand?.taskComponent.deadlineType) {
    case EABShorthandTaskDeadline.STATIC:
      return [
        DateTime.fromISO(resultIHConfig.value?.eabShorthand?.taskComponent.deadlineDate ?? "")
          .startOf("day")
          .toISO(),
        DateTime.fromISO(resultIHConfig.value?.eabShorthand?.taskComponent.deadlineDate ?? "")
          .endOf("day")
          .toISO(),
      ];
    case EABShorthandTaskDeadline.DYNAMIC:
      return [undefined, undefined];
    case EABShorthandTaskDeadline.FUTURE:
      return [
        DateTime.now()
          .plus({ days: resultIHConfig.value?.eabShorthand?.taskComponent.deadlineValue ?? 0 })
          .startOf("day")
          .toISO(),
        DateTime.now()
          .plus({ days: resultIHConfig.value?.eabShorthand?.taskComponent.deadlineValue ?? 0 })
          .endOf("day")
          .toISO(),
      ];
    default:
      return [DateTime.now().startOf("day").toISO(), DateTime.now().endOf("day").toISO()];
  }
});

function defaultIHOrder(): IHOrderEditType {
  return {
    technicalCategoryId: props.event?.technicalCategoryId,
    technicalPlaceId: props.event?.technicalPlaceId,
    assigneeId: resultIHConfig.value?.setResponsibleAsDefault
      ? resultIHConfig.value?.managerId
      : undefined,
    claimed: !(resultIHConfig.value?.hideResponsible ?? false),
    description: "",
    internalResourcesIds: [],
    equipmentsIds: [],
    plannedTime: 0,
    repTime: repTime.value.filter(notEmpty),
    status: TaskStatus.plan,
    fileIds: [],
    equipments: [],
    internalResources: [],
    preventsArchiving: false,
  };
}

const ihOrder = usePropsAsDefault({
  dynamicProps: computed(() =>
    props.action
      ? {
          ...(JSON.parse(JSON.stringify(props.action)) as ActionListActionFields_IHOrder_Fragment),
          fileIds: props.action.Folder?.files.map((file) => file.id) ?? [],
        }
      : defaultIHOrder(),
  ),
  replace: true,
});

provide(
  CreateInjectionKey,
  computed(() => props?.event?.id == null),
);

const { mutate: createIHOrderMutation, loading: loadingCreate } = useCreateIHOrderMutation({});
const { mutate: updateIHOrderMutation, loading: loadingUpdate } = useUpdateIHOrderMutation({});
const loading = computed(() => loadingCreate.value || loadingUpdate.value);
const eventType = computed(() => {
  return props.event.__typename as EventType;
});

/**
 * Create a new IHOrder
 */
const createIHOrder = async () => {
  if (
    !hasRight("sqdc.write", siteId.value) ||
    props.event.deletedAt != null ||
    observerValid.value !== true
  ) {
    return;
  }
  const res = await createIHOrderMutation(
    {
      eventType: eventType.value,
      eventId: props.event.id,
      ihOrder: {
        technicalPlaceId: ihOrder.value.technicalPlaceId!,
        technicalCategoryId: ihOrder.value.technicalCategoryId!,
        internalResourcesIds: ihOrder.value.internalResourcesIds,
        equipmentsIds: ihOrder.value.equipmentsIds,
        plannedTime: ihOrder.value.plannedTime,
        repTime: ihOrder.value.repTime.filter(notEmpty),
        description: ihOrder.value.description,
        status: ihOrder.value.status,
        assigneeId: ihOrder.value.assigneeId,
        claimed: ihOrder.value.claimed,
        fileIds: ihOrder.value.fileIds,
      },
    },
    {
      update: (cache, { data }) => {
        const storedObject = cache.identify({
          __typename: props.event.__typename,
          id: props.event.id,
        });
        if (storedObject && data != null) {
          addAction(storedObject, data.CreateIHOrder);
        }
      },
    },
  );

  if (res?.data?.CreateIHOrder == null) {
    throw Error("invalid response");
  }
  emit("save");
  await nextTick();
  emit("update:visible", false);
  actionModalVisible.value = false;
};

/**
 * Update an IHOrder
 */
const updateIHOrder = async () => {
  if (
    !hasRight("sqdc.write", siteId.value) ||
    props.event.deletedAt != null ||
    observerValid.value !== true
  ) {
    return;
  }
  await updateIHOrderMutation({
    ihOrderId: props.action.id,
    ihOrder: {
      technicalPlaceId: ihOrder.value.technicalPlaceId!,
      technicalCategoryId: ihOrder.value.technicalCategoryId!,
      internalResourcesIds: ihOrder.value.internalResourcesIds,
      equipmentsIds: ihOrder.value.equipmentsIds,
      plannedTime: ihOrder.value.plannedTime,
      repTime: ihOrder.value.repTime.filter(notEmpty),
      description: ihOrder.value.description,
      status: ihOrder.value.status,
      assigneeId: ihOrder.value.assigneeId,
      claimed: ihOrder.value.claimed,
      fileIds: ihOrder.value.fileIds,
    },
  });
  emit("updateAction");
  emit("update:visible", false);
  actionModalVisible.value = false;
};

/**
 * Save an IHOrder
 */
const saveIHOrder = computed(() => {
  return props.action ? updateIHOrder : createIHOrder;
});

/**
 * Show the modal
 */
const show = () => {
  if (!props.action) {
    if (props.event.__typename === EventType.IHFailureNotice && props.event?.planDate) {
      ihOrder.value.repTime = props.event.planDate;
    }
    if ("technicalCategoryId" in props.event && "technicalPlaceId" in props.event) {
      ihOrder.value.technicalCategoryId = props.event.technicalCategoryId;
      ihOrder.value.technicalPlaceId = props.event.technicalPlaceId;
    } else {
      ihOrder.value.technicalCategoryId =
        technicalPlaceIdForEventModal?.value.id ?? board?.value?.technicalPlaceId;
    }
  }
  actionModalVisible.value = true;
};

/**
 * Set the technical place
 * @param value
 */
const setTechnicalPlace = (value: number) => {
  props.action.technicalPlaceId = value;
};

/**
 * Set the technical category
 * @param value
 */
const setTechnicalCategory = (value: number) => {
  props.action.technicalCategoryId = value;
};

defineExpose({
  show,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
